import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

const LOGO_URL = "https://assets-h2.izmocars.com/dealerlogos/qcoresecure.com.png";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const NAV_LINKS = [
  { label: "Pricing", href: "#pricing" },
  { label: "Documentation", href: "/dashboard/api-docs" },
];


const STATS = [
  { value: "<5ms", label: "API LATENCY" },
  { value: "99.99%", label: "UPTIME SLA" },
  { value: "100x", label: "THROUGHPUT MULTIPLIER" },
  { value: "NIST", label: "SP 800-90A/B COMPLIANT" },
];

const PRICING = [
  {
    name: "DEVELOPER", price: "$49", period: "/month", desc: "For prototyping and development",
    features: ["10,000 API calls/month", "Raw QRNG entropy access", "1 API key", "Community support", "Basic analytics"],
    cta: "Start Free Trial", highlighted: false,
  },
  {
    name: "BUSINESS", price: "$499", period: "/month", desc: "For production workloads",
    features: ["1,000,000 API calls/month", "QRNG + DRBG expanded entropy", "10 API keys", "VRF service access", "Priority support", "Advanced analytics", "99.9% SLA"],
    cta: "Get Started", highlighted: true,
  },
  {
    name: "ENTERPRISE", price: "Custom", period: "", desc: "For mission-critical deployments",
    features: ["Unlimited API calls", "Dedicated entropy pool", "Unlimited API keys", "VRF + custom algorithms", "Dedicated support engineer", "On-premise appliance option", "99.99% SLA", "Custom compliance"],
    cta: "Contact Sales", highlighted: false,
  },
];

const COMPLIANCE = [
  "NIST SP 800-90A", "NIST SP 800-90B", "NIST SP 800-53", "FIPS 140-2/3", "ISO 27001", "RFC 8446 (TLS 1.3)", "X.509 v3 PKI",
];

function formatPrice(plan) {
  if (plan.price_monthly === 0) return plan.slug === "enterprise" ? "Custom" : "Free";
  return `$${plan.price_monthly % 1 === 0 ? plan.price_monthly.toFixed(0) : plan.price_monthly}`;
}

function getPricePeriod(plan) {
  if (plan.price_monthly === 0) return "";
  return "/month";
}

function getPlanCta(plan) {
  if (plan.slug === "enterprise") return "Contact Sales";
  if (plan.price_monthly === 0) return "Get Started Free";
  return "Get Started";
}

export default function LandingPage() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/plans/landing`)
      .then(r => r.json())
      .then(data => setPlans(Array.isArray(data) ? data.sort((a, b) => a.sort_order - b.sort_order) : []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#020617]/80 border-b border-white/5" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-10">
              <Link to="/" data-testid="logo-link">
                <img src={LOGO_URL} alt="QCoreSecure" className="h-14 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-8">
                {NAV_LINKS.map(l => (
                  <a key={l.label} href={l.href} className="text-sm text-slate-400 hover:text-white transition-colors tracking-wide uppercase" data-testid={`nav-${l.label.toLowerCase()}`}>
                    {l.label}
                  </a>
                ))}
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login" className="text-sm text-slate-300 hover:text-white transition-colors uppercase tracking-widest" data-testid="nav-login-btn">
                Sign In
              </Link>
              <Link to="/register" className="border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500 hover:text-black transition-all uppercase tracking-widest text-sm font-bold px-6 py-2" data-testid="nav-get-started-btn">
                Get Started
              </Link>
            </div>
            <button className="md:hidden text-slate-400" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          </div>
        </div>
        {mobileMenu && (
          <div className="md:hidden border-t border-white/5 bg-[#020617]/95 backdrop-blur-xl px-4 py-4 space-y-3">
            {NAV_LINKS.map(l => (
              <a key={l.label} href={l.href} className="block text-sm text-slate-400 py-2 uppercase tracking-wide">{l.label}</a>
            ))}
            <Link to="/login" className="block text-sm text-slate-300 py-2 uppercase tracking-wide">Sign In</Link>
            <Link to="/register" className="block border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 text-sm font-bold px-6 py-2 text-center uppercase tracking-widest mt-2">Get Started</Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-36 pb-24 lg:pt-48 lg:pb-32 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-grid-pattern opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 border border-cyan-500/20 bg-cyan-500/5 px-4 py-1.5 mb-8" data-testid="hero-badge">
              <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
              <span className="text-xs tracking-[0.15em] text-cyan-400 font-medium">QRaaS — Quantum Randomness as a Service</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-8" data-testid="hero-title">
              Ultra-Secure Quantum<br />
              <span className="text-slate-500">Randomness.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mb-4 leading-relaxed" data-testid="hero-subtitle">
              If it isn't Quantum, it isn't Random. QCoreSecure delivers true randomness, 
              generated from the fundamental laws of quantum physics. It's security you can't predict.
            </p>
            <p className="text-sm text-cyan-500/60 mb-12 italic">Powered by silicon-photonics QRNG technology</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500 hover:text-black transition-all uppercase tracking-widest text-sm font-bold px-8 py-3.5 group" data-testid="hero-cta-primary">
                Start Building <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-white/5 bg-slate-900/30 backdrop-blur" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <div key={i} className={`py-8 px-6 ${i > 0 ? 'border-l border-white/5' : ''} text-center`} data-testid={`stat-${i}`}>
                <div className="text-2xl md:text-3xl font-bold text-cyan-400 mb-1">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 lg:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-500 font-medium">PRICING</span>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mt-4">
              Quantum-Grade Entropy at Scale
            </h2>
          </div>
          {plansLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
            </div>
          ) : (
            <div className={`grid gap-px bg-white/5 max-w-6xl mx-auto ${plans.length <= 3 ? 'md:grid-cols-3' : plans.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
              {plans.filter(p => p.is_active && p.is_public).map((plan, i) => {
                const highlighted = plan.badge === "Most Popular" || plan.slug === "business";
                return (
                  <div key={plan.id} className={`p-8 lg:p-10 relative ${highlighted ? 'bg-slate-900/80 border border-cyan-500/20' : 'bg-[#020617]'}`} data-testid={`pricing-card-${plan.slug}`}>
                    {highlighted && (
                      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
                    )}
                    {plan.badge && (
                      <div className="text-[10px] uppercase tracking-[0.15em] text-cyan-400 border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 inline-block mb-3">{plan.badge}</div>
                    )}
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{plan.name}</div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-4xl font-bold">{formatPrice(plan)}</span>
                      <span className="text-sm text-slate-500">{getPricePeriod(plan)}</span>
                    </div>
                    <p className="text-sm text-slate-400 mb-8">{plan.description}</p>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                          <CheckCircle2 className="w-4 h-4 text-cyan-500/60 mt-0.5 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link to="/register" className={`block text-center uppercase tracking-widest text-sm font-bold px-6 py-3 transition-all ${highlighted ? 'border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500 hover:text-black' : 'border border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'}`} data-testid={`pricing-cta-${plan.slug}`}>
                      {getPlanCta(plan)}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Compliance */}
      <section className="py-16 border-t border-white/5" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500">COMPLIANCE & STANDARDS</span>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {COMPLIANCE.map((c, i) => (
              <div key={i} className="px-4 py-2 border border-white/5 bg-slate-900/30 text-xs uppercase tracking-wider text-slate-400 hover:border-cyan-500/20 hover:text-cyan-400 transition-all cursor-default" data-testid={`compliance-badge-${i}`}>
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6">
            Ready for Quantum-Grade Randomness?
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-10">
            Start with our free developer tier. No credit card required. 
            Get your first quantum random numbers from QCoreSecure in under 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 border border-cyan-500/50 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500 hover:text-black transition-all uppercase tracking-widest text-sm font-bold px-8 py-3.5" data-testid="cta-start-btn">
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="inline-flex items-center justify-center border border-slate-700 text-slate-400 hover:border-slate-500 transition-all uppercase tracking-widest text-sm px-8 py-3.5" data-testid="cta-demo-btn">
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src={LOGO_URL} alt="QCoreSecure" className="h-10 w-auto" />
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">If it isn't Quantum, it isn't Random.</p>
              <p className="text-[10px] text-slate-600 mt-1">AI Data Tech, LLC DBA QCoreSecure, San Francisco, CA</p>
            </div>
            {[
              { title: "PRODUCT", links: ["API Reference", "SDKs", "Pricing", "Status"] },
              { title: "COMPANY", links: ["About", "Blog", "Careers", "Contact"] },
              { title: "LEGAL", links: ["Privacy", "Terms", "Security", "Compliance"] },
            ].map((col, i) => (
              <div key={i}>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">{col.title}</div>
                <ul className="space-y-2">
                  {col.links.map((l, j) => (
                    <li key={j}><a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">{l}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <span className="text-xs text-slate-600">2026 AI Data Tech, LLC DBA QCoreSecure. All rights reserved.</span>
            <div className="flex gap-6">
              {["Twitter", "GitHub", "LinkedIn"].map(s => (
                <a key={s} href="#" className="text-xs text-slate-600 hover:text-slate-400 transition-colors uppercase tracking-wide">{s}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
